// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import axios from './axiosConfig';

const endpointErrors = {
  401: { message: 'Session expired. Please sign in again.', type: 'authentication' },
  403: { message: 'You are not permitted to check this Stargate.', type: 'authorization' },
  404: { message: 'Stargate not found', type: 'not_found' },
};

/**
 * Probe a configured Stargate through the authenticated backend.
 * @param {number} stargateId - Stored Stargate id
 * @param {number} maxAttempts - Maximum number of attempts (default 3)
 * @param {number} timeout - Timeout per attempt in ms (default 30000)
 */
export async function checkStargateHealth(stargateId, maxAttempts = 3, timeout = 30000) {
  if (!stargateId) {
    return {
      healthy: false,
      error: { message: 'Missing Stargate id', type: 'configuration' },
    };
  }

  let lastError = null;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const response = await axios.get('/health/stargate', {
        params: { stargate_id: stargateId, force_protocol: 'http' },
        timeout,
        // Keep 401 on the rejection path for axios session refresh.
        validateStatus: (status) => status !== 401,
        // The checker owns timeout/network attempts; session refresh still runs.
        _skipRetry: true,
      });
      if (endpointErrors[response.status]) {
        return {
          healthy: false,
          error: { ...endpointErrors[response.status], status: response.status },
        };
      }
      if (response.status === 200) {
        if (response.data.healthy) return { healthy: true };
        lastError = {
          message: response.data.error || 'Health check failed',
          type: response.data.errorType || 'unknown',
        };
        if (lastError.type === 'authentication' || lastError.type === 'configuration') break;
      } else {
        lastError = {
          message: response.data?.error || `Health check endpoint returned status: ${response.status}`,
          type:
            response.data?.errorType || (response.status >= 500 ? 'server_error' : 'http_error'),
          status: response.status,
        };
        if (lastError.type === 'configuration') break;
      }
    } catch (error) {
      // A 401 reaches here after axios has attempted session recovery.
      if (endpointErrors[error.response?.status]) {
        return {
          healthy: false,
          error: { ...endpointErrors[error.response.status], status: error.response.status },
        };
      }
      if (error.response) {
        lastError = {
          message: `Failed to connect to the Stargate (Error ${error.response.status}).`,
          type: error.response.status >= 500 ? 'server_error' : 'http_error',
          status: error.response.status,
        };
      } else if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
        lastError = {
          message: 'Connection timed out. The Stargate may be offline or unreachable.',
          type: 'timeout',
        };
      } else if (error.code === 'ERR_NETWORK' || error.code === 'ENOTFOUND') {
        lastError = { message: 'Network error. Unable to reach the Stargate.', type: 'network' };
      } else {
        lastError = { message: error.message || 'Unable to connect to the Stargate', type: 'unknown' };
      }
    }
  }
  return { healthy: false, error: lastError };
}

/**
 * Create a health check context that can be used by multiple components
 */
export class StargateHealthChecker {
  constructor(stargateId) {
    this.stargateId = stargateId;
    this.healthStatus = null;
    this.checkPromise = null;
    this.lastCheckTime = 0;
    this.checkInterval = 30000; // Base check interval
    this.retryCount = 0;
    this.maxRetryCount = 5; // Maximum retry count before giving up
    this.maxCheckInterval = 300000; // Maximum 5 minutes between checks
  }

  async getHealthStatus() {
    const now = Date.now();

    // Calculate dynamic check interval with exponential backoff
    const dynamicInterval = this.getDynamicInterval();

    // If we have a recent health check result, return it
    if (this.healthStatus && now - this.lastCheckTime < dynamicInterval) {
      return this.healthStatus;
    }

    // If a check is already in progress, wait for it
    if (this.checkPromise) {
      return this.checkPromise;
    }

    // Perform a new health check with fewer retries for periodic checks
    const timeout = 30000; // Use 30 second timeout for cellular networks
    const maxAttempts = this.retryCount > 0 ? 1 : 2; // Fewer attempts after initial check

    this.checkPromise = checkStargateHealth(this.stargateId, maxAttempts, timeout)
      .then((result) => {
        this.healthStatus = result;
        this.lastCheckTime = now;
        this.checkPromise = null;

        // Reset retry count on successful check
        if (result.healthy) {
          this.retryCount = 0;
        } else {
          // Increment retry count for failed checks (with cap)
          this.retryCount = Math.min(this.retryCount + 1, this.maxRetryCount);
        }

        return result;
      })
      .catch((error) => {
        // If checkStargateHealth throws, convert to a failed health status
        const errorResult = {
          healthy: false,
          error: {
            message: 'An unexpected error occurred while checking Stargate connectivity',
            type: 'unknown',
            originalError: error,
          },
        };
        this.healthStatus = errorResult;
        this.lastCheckTime = now;
        this.checkPromise = null;

        // Increment retry count for errors
        this.retryCount = Math.min(this.retryCount + 1, this.maxRetryCount);

        return errorResult;
      });

    return this.checkPromise;
  }

  getDynamicInterval() {
    // Implement exponential backoff
    // Base interval * (2 ^ retryCount), capped at max interval
    const backoffInterval = this.checkInterval * Math.pow(2, this.retryCount);
    return Math.min(backoffInterval, this.maxCheckInterval);
  }

  clearCache() {
    this.healthStatus = null;
    this.lastCheckTime = 0;
    this.checkPromise = null;
    this.retryCount = 0; // Reset retry count when manually clearing cache
  }
}
